
import { useState, useEffect } from 'react';

const testimonials = [
  {
    name: 'Dr. James Crawford',
    role: 'Orthopedic Surgeon, Baylor Scott & White',
    neighborhood: 'Highland Park',
    image: 'https://public.readdy.ai/ai/img_res/4e8f6c37d31bebed2cc6797e11fe38c0.jpg',
    quote: 'As a surgeon, I understand the importance of comprehensive health coverage. Dallas Health Insurance Experts found my family a premium plan that covers every specialist we need at Baylor and UT Southwestern. Their concierge approach saved us time and thousands of dollars.',
    rating: 5
  },
  {
    name: 'Catherine & William Sterling',
    role: 'Private Equity Partners',
    neighborhood: 'Preston Hollow',
    image: 'https://readdy.ai/api/search-image?query=elegant%20wealthy%20couple%20in%20their%20late%2040s%2C%20sophisticated%20business%20attire%2C%20warm%20confident%20smiles%2C%20luxury%20modern%20home%20interior%20background%2C%20affluent%20professional%20portrait%2C%20soft%20golden%20lighting&width=400&height=400&seq=test-couple-sterling-2&orientation=squarish',
    quote: 'When we moved to Preston Hollow, finding the right health insurance for our family of five was overwhelming. This team compared dozens of premium plans and found coverage that includes our preferred physicians and concierge medicine. Absolutely first-class service.',
    rating: 5
  },
  {
    name: 'Dr. Ananya Patel',
    role: 'Pediatrician, Children\'s Medical Center',
    neighborhood: 'University Park',
    image: 'https://readdy.ai/api/search-image?query=professional%20female%20pediatrician%20doctor%20in%20her%2030s%20wearing%20white%20coat%2C%20warm%20approachable%20smile%2C%20modern%20pediatric%20office%20background%2C%20confident%20caring%20physician%20portrait%2C%20bright%20natural%20lighting&width=400&height=400&seq=test-pediatrician-ananya-3&orientation=squarish',
    quote: 'Both my personal family coverage and my practice\'s group plan are handled by Dallas Health Insurance Experts. They understand the unique needs of medical professionals and delivered plans that protect my staff and my family beautifully.',
    rating: 5
  },
  {
    name: 'Robert & Lisa Thornton',
    role: 'CEO, Thornton Energy Group',
    neighborhood: 'Highland Park',
    image: 'https://readdy.ai/api/search-image?query=successful%20distinguished%20business%20couple%20in%20their%2050s%2C%20elegant%20casual%20attire%2C%20warm%20genuine%20smiles%2C%20beautiful%20upscale%20garden%20terrace%20background%2C%20affluent%20lifestyle%20portrait%2C%20warm%20natural%20sunlight&width=400&height=400&seq=test-ceo-thornton-4&orientation=squarish',
    quote: 'We\'ve been clients for over eight years. They handle our personal coverage, our company\'s group plan for 45 employees, and even helped our adult children find individual plans. The level of personal attention is unmatched in Dallas.',
    rating: 5
  },
  {
    name: 'Dr. Marcus Williams',
    role: 'Cardiologist, Texas Health Presbyterian',
    neighborhood: 'Park Cities',
    image: 'https://readdy.ai/api/search-image?query=distinguished%20African%20American%20male%20cardiologist%20in%20his%2040s%20wearing%20white%20coat%20with%20stethoscope%2C%20confident%20professional%20smile%2C%20modern%20cardiology%20office%20background%2C%20trustworthy%20physician%20portrait%2C%20clean%20bright%20lighting&width=400&height=400&seq=test-cardio-marcus-5&orientation=squarish',
    quote: 'Finding health insurance that truly covers the needs of a physician\'s family required expertise I didn\'t have time to develop myself. This agency understood exactly what we needed and delivered a plan that gives us complete peace of mind.',
    rating: 5
  }
];

export default function TestimonialCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-sm font-semibold tracking-widest text-secondary uppercase mb-4">Client Testimonials</span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-primary mb-4">
            What Dallas&apos;s <strong>Leading Families &amp; Physicians</strong> Say
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Hear from Highland Park doctors, Preston Hollow executives, and Park Cities families who trust us with their health insurance needs.
          </p>
        </div>

        {/* Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-sky-50 to-emerald-50 rounded-2xl p-10 md:p-12 shadow-xl">
            {/* Quote Icon */}
            <div className="absolute top-8 left-8 text-sky-200 opacity-50">
              <i className="ri-double-quotes-l text-6xl"></i>
            </div>

            {/* Testimonial Content */}
            <div className="relative z-10">
              <div className="flex flex-col md:flex-row items-center gap-8 mb-8">
                <div className="w-24 h-24 rounded-full overflow-hidden shadow-lg flex-shrink-0 ring-4 ring-white">
                  <img
                    src={testimonials[currentIndex].image}
                    alt={`${testimonials[currentIndex].name} - ${testimonials[currentIndex].role} health insurance client`}
                    title={`${testimonials[currentIndex].neighborhood} health insurance testimonial`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div className="text-center md:text-left">
                  <h3 className="text-2xl font-bold text-primary mb-1">
                    {testimonials[currentIndex].name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-1">{testimonials[currentIndex].role}</p>
                  <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                    <i className="ri-map-pin-2-line text-secondary text-sm"></i>
                    <span className="text-xs font-semibold text-secondary uppercase tracking-wide">{testimonials[currentIndex].neighborhood}</span>
                  </div>
                  <div className="flex gap-1 justify-center md:justify-start">
                    {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-base"></i>
                    ))}
                  </div>
                </div>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed italic">
                &ldquo;{testimonials[currentIndex].quote}&rdquo;
              </p>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="whitespace-nowrap absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 flex items-center justify-center bg-white hover:bg-gray-50 rounded-full shadow-lg transition-all duration-300 cursor-pointer"
            aria-label="Previous testimonial"
          >
            <i className="ri-arrow-left-s-line text-2xl text-primary"></i>
          </button>

          <button
            onClick={nextSlide}
            className="whitespace-nowrap absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 flex items-center justify-center bg-white hover:bg-gray-50 rounded-full shadow-lg transition-all duration-300 cursor-pointer"
            aria-label="Next testimonial"
          >
            <i className="ri-arrow-right-s-line text-2xl text-primary"></i>
          </button>

          {/* Dots Navigation */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`whitespace-nowrap w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentIndex ? 'bg-secondary w-8' : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
